import type { ReactNode } from "react";
import { Tag, Typography, Progress, Space, Tooltip } from "antd";
import { DateField } from "@refinedev/antd";
import { useTranslation } from "react-i18next";
import type { FormFieldMetadata } from "../../hooks/useFormMetadata";

const { Text } = Typography;

export interface DynamicFormFieldDisplayProps {
  /** Field metadata */
  field: FormFieldMetadata;
  /** Field value to display */
  value: unknown;
  /** Display label for item reference fields (from API's {field}Display property) */
  displayValue?: string;
}

/**
 * Renders a read-only display value for a form field.
 * Used in "show" mode to display values instead of form inputs.
 */
export const DynamicFormFieldDisplay: React.FC<
  DynamicFormFieldDisplayProps
> = ({ field, value, displayValue }) => {
  const { t } = useTranslation();
  const { dataType, options, mappedName } = field;

  // Handle null/undefined values
  if (value === null || value === undefined || value === "") {
    return <Text type="secondary">-</Text>;
  }

  // Currency fields get special formatting
  const isCurrencyField =
    mappedName === "estimatedValue" ||
    mappedName === "amount" ||
    mappedName === "totalRevenue" ||
    mappedName === "annualRevenue";

  // Probability field gets progress bar
  const isProbabilityField = mappedName === "probability";

  // Normalize data type
  const normalizedType = dataType.toLowerCase().replace(/\s+/g, "_");

  // Render based on data type
  const renderValue = (): ReactNode => {
    switch (normalizedType) {
      case "string": {
        const strVal = String(value);
        // Auto-detect URLs and render as clickable links
        if (strVal.startsWith("http://") || strVal.startsWith("https://")) {
          return (
            <a href={strVal} target="_blank" rel="noopener noreferrer">
              {strVal}
            </a>
          );
        }
        return <Text>{strVal}</Text>;
      }

      case "text":
        // Multi-line text with proper wrapping
        return (
          <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {String(value)}
          </Text>
        );

      case "integer":
        if (isProbabilityField) {
          return <Progress percent={Number(value)} size="small" />;
        }
        return <Text>{Number(value).toLocaleString()}</Text>;

      case "decimal":
      case "float":
        if (isCurrencyField) {
          return (
            <Text>
              ${" "}
              {Number(value).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </Text>
          );
        }
        return (
          <Text>
            {Number(value).toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </Text>
        );

      case "date":
        return <DateField value={value as string} format="YYYY-MM-DD" />;

      case "datetime":
        return <DateField value={value as string} format="YYYY-MM-DD HH:mm" />;

      case "time": {
        // Format time value to HH:mm
        const strValue = String(value);
        try {
          // Handle ISO datetime string (e.g., "2026-02-02T00:53:00")
          if (strValue.includes("T")) {
            const timePart = strValue.split("T")[1];
            const [hours, minutes] = timePart.split(":");
            return <Text>{`${hours}:${minutes}`}</Text>;
          }
          // Handle time string (HH:mm:ss or HH:mm)
          const [hours, minutes] = strValue.split(":");
          return <Text>{`${hours}:${minutes}`}</Text>;
        } catch {
          return <Text>{strValue}</Text>;
        }
      }

      case "boolean":
        return (
          <Tag color={value ? "green" : "default"}>
            {value ? t("common.yes", "Yes") : t("common.no", "No")}
          </Tag>
        );

      case "list":
      case "filter_list":
        // Look up label from options if available
        // Use String() coercion for comparison (value may be number, options are strings)
        if (options && options.length > 0) {
          const valueStr = String(value);
          const option = options.find((opt) => String(opt.value) === valueStr);
          const label = option?.label || valueStr;
          return <Tag>{label}</Tag>;
        }
        return <Tag>{String(value)}</Tag>;

      case "color":
        return (
          <Space>
            <span
              style={{
                display: "inline-block",
                width: 16,
                height: 16,
                backgroundColor: String(value),
                borderRadius: 2,
                border: "1px solid #d9d9d9",
              }}
            />
            <Text>{String(value)}</Text>
          </Space>
        );

      case "color_list":
        // Look up label and show color chip
        if (options && options.length > 0) {
          const option = options.find(
            (opt) => String(opt.value) === String(value),
          );
          const label = option?.label || String(value);
          return (
            <Space>
              <span
                style={{
                  display: "inline-block",
                  width: 16,
                  height: 16,
                  backgroundColor: String(value),
                  borderRadius: 2,
                  border: "1px solid #d9d9d9",
                }}
              />
              <Text>{label}</Text>
            </Space>
          );
        }
        return <Text>{String(value)}</Text>;

      case "item": {
        // Display keyed_name or name of related item
        // The value is typically the ID, but options contain the label
        const itemOption = options?.find(
          (opt) => String(opt.value) === String(value),
        );
        const itemLabel = itemOption?.label || displayValue;
        if (itemLabel) {
          return (
            <Tooltip title={`ID: ${value}`}>
              <Text>{itemLabel}</Text>
            </Tooltip>
          );
        }
        // Fallback to showing the value (ID or keyed_name)
        return <Text>{String(value)}</Text>;
      }

      case "mv_list":
      case "multi_value_list":
        // Multi-value list - display as multiple tags
        if (Array.isArray(value)) {
          return (
            <Space wrap>
              {value.map((v, index) => {
                const vStr = String(v);
                const label =
                  options?.find((opt) => String(opt.value) === vStr)?.label ||
                  vStr;
                return <Tag key={index}>{label}</Tag>;
              })}
            </Space>
          );
        }
        // If stored as comma-separated string
        if (typeof value === "string" && value.includes(",")) {
          const values = value.split(",").map((v) => v.trim());
          return (
            <Space wrap>
              {values.map((v, index) => {
                const label =
                  options?.find((opt) => String(opt.value) === v)?.label || v;
                return <Tag key={index}>{label}</Tag>;
              })}
            </Space>
          );
        }
        return <Tag>{String(value)}</Tag>;

      case "sequence":
        // Auto-generated sequence number
        return <Text code>{String(value)}</Text>;

      case "image":
        // Display as image or link
        return (
          <a href={String(value)} target="_blank" rel="noopener noreferrer">
            <img
              src={String(value)}
              alt={field.label || field.mappedName}
              style={{ maxWidth: 200, maxHeight: 100 }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement!.textContent = String(value);
              }}
            />
          </a>
        );

      case "md5":
        // Password/hash - show masked
        return <Text>••••••••</Text>;

      default:
        // Fallback to text display
        return <Text>{String(value)}</Text>;
    }
  };

  return <>{renderValue()}</>;
};
